/**
 * Fleet Vehicle Management Slice
 *
 * State for the fleet portal's "My Vehicles" page (CLIENT users):
 * - Paginated, rego-filterable list of the fleet customer's own vehicles
 * - Add a vehicle
 * - Remove a vehicle
 *
 * Everything is scoped server-side by the customerUuid JWT claim — the client
 * never sends a customer id.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    listFleetVehiclesApi,
    createFleetVehicleApi,
    deleteFleetVehicleApi,
} from '../../services/fleetVehicleMgtApi.js';

// ============================================
// Async Thunks
// ============================================

export const fetchFleetVehicles = createAsyncThunk(
    'fleetVehicles/fetchFleetVehicles',
    async ({ page = 0, size = 20, rego = '' } = {}, { rejectWithValue }) => {
        try {
            const data = await listFleetVehiclesApi({ page, size, rego });
            return { data, page, size, rego };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load vehicles.';
            return rejectWithValue(message);
        }
    }
);

export const createFleetVehicle = createAsyncThunk(
    'fleetVehicles/createFleetVehicle',
    async (payload, { rejectWithValue }) => {
        try {
            return await createFleetVehicleApi(payload);
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to add vehicle.';
            return rejectWithValue(message);
        }
    }
);

export const deleteFleetVehicle = createAsyncThunk(
    'fleetVehicles/deleteFleetVehicle',
    async (vehicleUuid, { rejectWithValue }) => {
        try {
            await deleteFleetVehicleApi(vehicleUuid);
            return vehicleUuid;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to remove vehicle.';
            return rejectWithValue(message);
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    // List
    items: [],
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    rego: '',
    loading: false,
    error: null,

    // Add
    creating: false,
    createError: null,

    // Delete — uuid of the row currently being removed
    deletingUuid: null,
    deleteError: null,
};

const fleetVehicleSlice = createSlice({
    name: 'fleetVehicles',
    initialState,
    reducers: {
        setFleetVehicleRegoFilter(state, action) {
            state.rego = action.payload;
        },
        clearFleetVehicleErrors(state) {
            state.error = null;
            state.createError = null;
            state.deleteError = null;
        },
    },
    extraReducers: (builder) => {
        // ---- List ----
        builder
            .addCase(fetchFleetVehicles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFleetVehicles.fulfilled, (state, action) => {
                const { data, page, size, rego } = action.payload;
                state.loading = false;
                state.items = data.content || [];
                state.page = data.page ?? page;
                state.size = data.size ?? size;
                state.totalElements = data.totalElements ?? 0;
                state.totalPages = data.totalPages ?? 0;
                state.rego = rego;
            })
            .addCase(fetchFleetVehicles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load vehicles.';
            });

        // ---- Create ----
        builder
            .addCase(createFleetVehicle.pending, (state) => {
                state.creating = true;
                state.createError = null;
            })
            .addCase(createFleetVehicle.fulfilled, (state) => {
                state.creating = false;
            })
            .addCase(createFleetVehicle.rejected, (state, action) => {
                state.creating = false;
                state.createError = action.payload || 'Failed to add vehicle.';
            });

        // ---- Delete ----
        builder
            .addCase(deleteFleetVehicle.pending, (state, action) => {
                state.deletingUuid = action.meta.arg;
                state.deleteError = null;
            })
            .addCase(deleteFleetVehicle.fulfilled, (state) => {
                state.deletingUuid = null;
            })
            .addCase(deleteFleetVehicle.rejected, (state, action) => {
                state.deletingUuid = null;
                state.deleteError = action.payload || 'Failed to remove vehicle.';
            });
    },
});

export const {
    setFleetVehicleRegoFilter,
    clearFleetVehicleErrors,
} = fleetVehicleSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectFleetVehicles = (state) => state.fleetVehicles.items;
export const selectFleetVehiclesLoading = (state) => state.fleetVehicles.loading;
export const selectFleetVehiclesError = (state) => state.fleetVehicles.error;
export const selectFleetVehiclesPage = (state) => state.fleetVehicles.page;
export const selectFleetVehiclesTotalPages = (state) => state.fleetVehicles.totalPages;
export const selectFleetVehiclesTotalElements = (state) => state.fleetVehicles.totalElements;
export const selectFleetVehicleRegoFilter = (state) => state.fleetVehicles.rego;
export const selectFleetVehicleCreating = (state) => state.fleetVehicles.creating;
export const selectFleetVehicleCreateError = (state) => state.fleetVehicles.createError;
export const selectFleetVehicleDeletingUuid = (state) => state.fleetVehicles.deletingUuid;
export const selectFleetVehicleDeleteError = (state) => state.fleetVehicles.deleteError;

export default fleetVehicleSlice.reducer;